'use client';

/**
 * Carousel - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Carousel (Embla) with additional convenience props
 * for common use cases.
 *
 * Enhancements over shadcn/ui:
 * - CarouselItem basis prop for easy width control (e.g., '1/3', '1/2', 'full')
 * - CarouselPrevious/Next show prop to conditionally hide navigation
 * - Simplified orientation prop (horizontal/vertical)
 * - Direct access to Embla plugins and options
 * - setApi callback for external carousel control
 *
 * Sub-components:
 * - Carousel: Container with orientation, opts, plugins, setApi
 * - CarouselContent: Scrollable content wrapper
 * - CarouselItem: Individual slide with basis prop
 * - CarouselPrevious/CarouselNext: Navigation buttons with show prop
 *
 * @see {@link file://./../ui/carousel.tsx} for the underlying implementation
 */
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type ComponentRef, type ReactNode } from 'react';
import {
    Carousel as ShadcnCarousel,
    CarouselContent as ShadcnCarouselContent,
    CarouselItem as ShadcnCarouselItem,
    CarouselPrevious as ShadcnCarouselPrevious,
    CarouselNext as ShadcnCarouselNext,
    type CarouselApi,
} from '@/app/_components/ui/carousel';
import {cn} from '@/app/_lib/utils';

export type CarouselOrientation = 'horizontal' | 'vertical';

export interface CarouselProps {
    /**
     * Carousel orientation
     * @default 'horizontal'
     */
    orientation?: CarouselOrientation;
    /**
     * Embla carousel options
     */
    opts?: Parameters<typeof ShadcnCarousel>[0]['opts'];
    /**
     * Additional Embla carousel plugins (autoplay/fade are handled via props)
     */
    plugins?: Parameters<typeof ShadcnCarousel>[0]['plugins'];
    /**
     * Enable autoplay
     * @default false
     */
    autoplay?: boolean;
    /**
     * Autoplay delay in milliseconds
     * @default 4000
     */
    autoplayDelay?: number;
    /**
     * Pause autoplay on mouse hover
     * @default true
     */
    stopOnMouseEnter?: boolean;
    /**
     * Enable fade transition (only works with single slide view)
     * @default false
     */
    fade?: boolean;
    /**
     * Callback to get the carousel API instance
     */
    setApi?: (api: CarouselApi) => void;
    /**
     * Accessible label for the carousel region
     */
    ariaLabel?: string;
    /**
     * Custom className for styling
     */
    className?: string;
    /**
     * Carousel content
     */
    children?: ReactNode;
}

export interface CarouselContentProps {
    /**
     * Custom className for styling
     */
    className?: string;
    /**
     * Content items
     */
    children?: ReactNode;
}

export interface CarouselItemProps {
    /**
     * Custom className for styling
     */
    className?: string;
    /**
     * Item content
     */
    children?: ReactNode;
    /**
     * Basis for item sizing (e.g., '1/3', '1/2', 'full')
     * @default 'full'
     */
    basis?: string;
}

export interface CarouselNavigationProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
    /**
     * Whether to show the navigation button
     * @default true
     */
    show?: boolean;
    /**
     * Button variant
     * @default 'outline'
     */
    variant?:
        | 'default'
        | 'destructive'
        | 'outline'
        | 'secondary'
        | 'ghost'
        | 'link';
    /**
     * Button size
     * @default 'icon'
     */
    size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
    (
        {
            orientation = 'horizontal',
            opts,
            plugins: externalPlugins,
            autoplay = false,
            autoplayDelay = 4000,
            stopOnMouseEnter = true,
            fade = false,
            setApi: externalSetApi,
            ariaLabel,
            className,
            children,
        },
        ref
    ) => {
        const [internalApi, setInternalApi] = useState<CarouselApi>();
        const localRef = useRef<HTMLDivElement>(null);
        const hasPlayedOnce = useRef(false);

        const mergedRef = useCallback(
            (node: HTMLDivElement | null) => {
                localRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref]
        );

        const handleSetApi = useCallback(
            (api: CarouselApi) => {
                setInternalApi(api);
                externalSetApi?.(api);
            },
            [externalSetApi]
        );

        const plugins = useMemo(() => {
            const pluginList = [...(externalPlugins ?? [])];
            if (fade) {
                pluginList.push(Fade());
            }
            if (autoplay) {
                pluginList.push(
                    Autoplay({
                        delay: autoplayDelay,
                        stopOnInteraction: false,
                        stopOnMouseEnter,
                        playOnInit: false,
                    })
                );
            }
            return pluginList;
        }, [externalPlugins, autoplay, autoplayDelay, stopOnMouseEnter, fade]);

        // Start/stop autoplay based on viewport visibility
        useEffect(() => {
            if (!autoplay || !internalApi) return;

            const autoplayPlugin = internalApi.plugins().autoplay as
                | { play?: () => void; stop?: () => void }
                | undefined;
            if (!autoplayPlugin) return;

            const node = localRef.current;
            if (!node) return;

            let initialDelayTimer: ReturnType<typeof setTimeout>;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        if (!hasPlayedOnce.current) {
                            initialDelayTimer = setTimeout(() => {
                                autoplayPlugin.play?.();
                                hasPlayedOnce.current = true;
                            }, 4000);
                        } else {
                            autoplayPlugin.play?.();
                        }
                    } else {
                        clearTimeout(initialDelayTimer);
                        autoplayPlugin.stop?.();
                    }
                },
                { threshold: 0.3 }
            );

            observer.observe(node);

            return () => {
                observer.disconnect();
                clearTimeout(initialDelayTimer);
            };
        }, [autoplay, internalApi]);

        return (
            <ShadcnCarousel
                ref={mergedRef}
                orientation={orientation}
                opts={opts}
                plugins={plugins}
                setApi={handleSetApi}
                aria-label={ariaLabel}
                className={cn(className)}
            >
                {children}
            </ShadcnCarousel>
        );
    }
);

export const CarouselContent = forwardRef<
    ComponentRef<typeof ShadcnCarouselContent>,
    CarouselContentProps
>(({className, children}, ref) => {
    return (
        <ShadcnCarouselContent ref={ref} className={cn(className)}>
      {children}
    </ShadcnCarouselContent>
    );
});

// Static mapping to avoid Tailwind purging dynamically composed classes
const basisClasses: Record<string, string> = {
    'full': 'basis-full',
    '1/2': 'basis-1/2',
    '1/3': 'basis-1/3',
    '1/4': 'basis-1/4',
    '1/5': 'basis-1/5',
};

export const CarouselItem = forwardRef<
    ComponentRef<typeof ShadcnCarouselItem>,
    CarouselItemProps
>(({className, basis = 'full', children}, ref) => {
    return (
        <ShadcnCarouselItem
            ref={ref}
            className={cn(basis !== 'full' && (basisClasses[basis] || `basis-${basis}`), className)}
        >
      {children}
    </ShadcnCarouselItem>
    );
});

export const CarouselPrevious = forwardRef<
    ComponentRef<typeof ShadcnCarouselPrevious>,
    CarouselNavigationProps
>(
    (
        {className, show = true, variant = 'outline', size = 'icon', ...props},
        ref
    ) => {
        if (!show) return null;

        return (
            <ShadcnCarouselPrevious
                ref={ref}
                variant={variant}
                size={size}
                className={cn(className)}
                {...props}
            />
        );
    }
);

export const CarouselNext = forwardRef<
    ComponentRef<typeof ShadcnCarouselNext>,
    CarouselNavigationProps
>(
    (
        {className, show = true, variant = 'outline', size = 'icon', ...props},
        ref
    ) => {
        if (!show) return null;

        return (
            <ShadcnCarouselNext
                ref={ref}
                variant={variant}
                size={size}
                className={cn(className)}
                {...props}
            />
        );
    }
);

Carousel.displayName = 'Carousel';
CarouselContent.displayName = 'CarouselContent';
CarouselItem.displayName = 'CarouselItem';
CarouselPrevious.displayName = 'CarouselPrevious';
CarouselNext.displayName = 'CarouselNext';

export default Carousel;
export type {CarouselApi};